import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAstrologerProfile } from '../../hooks/useAstrologerProfile';
import type { AstrologerAnalysis, AstrologerClient } from '../../types/professional';

export function ProfessionalHistory() {
  const { astrologerProfile, loading } = useAstrologerProfile();
  const [analyses, setAnalyses]   = useState<(AstrologerAnalysis & { client_name?: string })[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [filterService, setFilterService] = useState('');

  useEffect(() => {
    if (!astrologerProfile) {
      if (!loading) setLoadingData(false);
      return;
    }
    Promise.all([
      supabase.from('astrologer_client_analyses').select('*').eq('astrologer_id', astrologerProfile.id).order('created_at', { ascending: false }).limit(200),
      supabase.from('astrologer_clients').select('id, name').eq('astrologer_id', astrologerProfile.id),
    ]).then(([{ data: a }, { data: c }]) => {
      const clientMap: Record<string, string> = {};
      for (const cl of (c ?? []) as AstrologerClient[]) clientMap[cl.id] = cl.name;
      const list = ((a ?? []) as AstrologerAnalysis[]).map(an => ({ ...an, client_name: clientMap[an.client_id] ?? 'Cliente' }));
      setAnalyses(list);
      setLoadingData(false);
    });
  }, [astrologerProfile?.id, loading]);

  if (loading || loadingData) return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Carregando histórico...</div>;

  const services = [...new Set(analyses.map(a => a.service_name))].sort();
  const filtered = filterService ? analyses.filter(a => a.service_name === filterService) : analyses;

  return (
    <div>
      <h1 style={{ fontSize: 'clamp(1.25rem,3vw,1.5rem)', color: 'var(--text-primary)', margin: '0 0 1.5rem', fontFamily: 'var(--font-display)' }}>
        Histórico de Análises
      </h1>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <select className="form-input" style={{ width: 'auto', fontSize: 'var(--text-caption)' }} value={filterService} onChange={e => setFilterService(e.target.value)}>
          <option value="">Todos os serviços</option>
          {services.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', alignSelf: 'center' }}>
          {filtered.length} análise{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
          Nenhuma análise encontrada.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {filtered.map(a => (
            <div key={a.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: '0 0 0.2rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
                  {a.service_name}, {a.client_name}
                </p>
                <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                  {new Date(a.created_at).toLocaleString('pt-BR')}
                  {a.doc_downloads ? ` · ${a.doc_downloads} download${a.doc_downloads !== 1 ? 's' : ''}` : ''}
                  {a.tokens_used ? ` · ${a.tokens_used} tokens` : ''}
                </p>
              </div>
              <span style={{ fontSize: 'var(--text-caption)', color: a.status === 'completed' ? '#4ade80' : a.status === 'error' ? '#f87171' : '#fbbf24', flexShrink: 0 }}>
                {a.status === 'completed' ? '✅' : a.status === 'error' ? '❌' : '⏳'} {a.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
